import React, { useRef, useEffect } from "react";
import { Container, Typography, Box, Button, Paper } from "@mui/material";
import { Icon } from "@iconify/react";
import ImpossibleButton from "../components/ImpossibleButton";

const Home = () => {
  const secondSectionRef = useRef(null);

  const scrollToSecondSection = (e) => {
    e.preventDefault();
    if (secondSectionRef.current) {
      secondSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // We can keep this effect for additional browser support
  useEffect(() => {
    // Add smooth scrolling to all browsers
    document.documentElement.style.scrollBehavior = "smooth";

    return () => {
      // Clean up
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* First section - Hero (100dvh) */}
      <Box
        sx={{
          height: "100dvh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: 'url("/background.webp")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
        }}
      >
        <Container>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Oswald", sans-serif',
                fontSize: { xs: "1rem", sm: "1.5rem", md: "1.8rem" },
                color: "#fff",
                letterSpacing: "30px",
                textTransform: "uppercase",
                fontWeight: 500,
                textShadow: "0 7px hsla(0,0%,0%,.4)",
              }}
            >
              THE ORDER OF GAMING
            </Typography>

            <Typography
              variant="h1"
              sx={{
                fontFamily: "Refault",
                fontSize: { xs: "5rem", sm: "7rem", md: "9rem" },
                color: "#fff",
                textTransform: "uppercase",
                letterSpacing: "0.2em",
                marginBottom: 2,
                textShadow: "0 7px hsla(0,0%,0%,.4)",
              }}
            >
              #TOG
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                marginTop: -1,
              }}
            >
              <Button
                variant="contained"
                startIcon={
                  <Icon icon="ic:baseline-discord" width="24" height="24" />
                }
                component="a"
                href="https://discord.gg/togx"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: "#5865F2",
                  "&:hover": {
                    backgroundColor: "#4752C4",
                  },
                  textTransform: "none",
                  padding: "10px 24px",
                  fontSize: "1rem",
                  color: "#ffffff",
                  fontWeight: 600,
                  clipPath:
                    "polygon(90% 0, 100% 34%, 100% 100%, 10% 100%, 0 66%, 0 0)",
                }}
              >
                JOIN US!
              </Button>
              <Button
                variant="contained"
                onClick={scrollToSecondSection}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                  textTransform: "none",
                  padding: "10px 24px",
                  fontSize: "1rem",
                  color: "#ffffff",
                  fontWeight: 600,
                  clipPath:
                    "polygon(10% 0, 100% 0, 100% 66%, 90% 100%, 0 100%, 0 34%)",
                }}
              >
                DREAM MAKING
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Second section - Welcome (100dvh) */}
      <Box
        ref={secondSectionRef}
        id="second-section"
        sx={{
          minHeight: "100dvh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: 'url("/section-wrapper-bg.webp")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: { xs: 4, md: 8 },
            }}
          >
            {/* Left side - Text content */}
            <Paper
              elevation={0}
              sx={{
                backgroundColor: "rgba(40, 44, 52, 0.9)",
                padding: 4,
                borderRadius: 2,
                maxWidth: { xs: "100%", md: "50%" },
                flex: 1,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  fontFamily: '"Oswald", sans-serif',
                  fontSize: "0.9rem",
                  color: "#c4d4e0",
                  letterSpacing: "2px",
                  display: "block",
                }}
              >
                THE ORDER OF GAMING
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontFamily: '"Oswald", sans-serif',
                  fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
                  color: "#fff",
                  textTransform: "uppercase",
                  fontWeight: 500,
                  marginBottom: 2,
                }}
              >
                WELCOME TO A NEW WORLD OF{" "}
                <span style={{ color: "#c4d4e0" }}>GAMING</span>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#fff",
                  opacity: 0.8,
                  marginBottom: 2,
                  lineHeight: 1.6,
                }}
              >
                Welcome to TOG, here we are different, we are not a "clan". We
                are a community. We are a family. We care about our users and
                strive to grow together and better each other as a whole. We
                make sure that you have the things you need in order to succeed
                and help us succeed. This is what we aim for and want to see the
                gaming community grow as a whole around the globe.
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                <Icon
                  icon="mdi:arrow-right-circle-outline"
                  width="20"
                  height="20"
                  style={{ color: "#c4d4e0", marginRight: "8px" }}
                />
                <Typography variant="body2" sx={{ color: "#fff" }}>
                  We are The Order of Gaming. We are TOG.
                </Typography>
              </Box>
            </Paper>

            {/* Right side - Logo */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src="/logo/logolarge.webp"
                alt="TOG Logo"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  maxHeight: "300px",
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Third section - Supported Games (100dvh) */}
      <Box
        sx={{
          minHeight: "100dvh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: 'url("/section-wrapper-bg.webp")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              mb: 4,
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Oswald", sans-serif',
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                color: "#fff",
                textTransform: "uppercase",
                fontWeight: 500,
                textAlign: "center",
                position: "relative",
                mb: 6,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-16px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "60px",
                  height: "4px",
                  backgroundColor: "#c4d4e0",
                },
              }}
            >
              OUR SUPPORTED GAMES
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr 1fr",
                },
                gap: 3,
                width: "100%",
              }}
            >
              {/* Game 1 - Call of Duty */}
              <Box
                component="a"
                href="https://discord.gg/Hfp6tyvksq"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%", // 16:9 aspect ratio
                  overflow: "hidden",
                  borderRadius: "4px",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05) translateY(-5px)",
                    boxShadow:
                      "0 10px 20px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                    zIndex: 1,
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "30%",
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                    zIndex: 1,
                  },
                  "&:hover::after": {
                    opacity: 1,
                  },
                }}
              >
                <img
                  src="/games/bo6.webp"
                  alt="Call of Duty: Black Ops"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.3s ease",
                  }}
                />
              </Box>

              {/* Game 2 - R6 */}
              <Box
                component="a"
                href="https://discord.gg/uHjPgsfuhj"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%", // 16:9 aspect ratio
                  overflow: "hidden",
                  borderRadius: "4px",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05) translateY(-5px)",
                    boxShadow:
                      "0 10px 20px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                    zIndex: 1,
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "30%",
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                    zIndex: 1,
                  },
                  "&:hover::after": {
                    opacity: 1,
                  },
                }}
              >
                <img
                  src="/games/R6.webp"
                  alt="Rainbow Six Siege"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.3s ease",
                  }}
                />
              </Box>

              {/* Game 3 - Minecraft */}
              <Box
                component="a"
                href="https://discord.gg/Tz7WGyE7U5"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%", // 16:9 aspect ratio
                  overflow: "hidden",
                  borderRadius: "4px",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05) translateY(-5px)",
                    boxShadow:
                      "0 10px 20px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                    zIndex: 1,
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "30%",
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                    zIndex: 1,
                  },
                  "&:hover::after": {
                    opacity: 1,
                  },
                }}
              >
                <img
                  src="/games/minecraft.webp"
                  alt="Minecraft"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>

              {/* Game 4 - Forza Horizon */}
              <Box
                component="a"
                href="https://discord.gg/VZ2byh3PEh"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%", // 16:9 aspect ratio
                  overflow: "hidden",
                  borderRadius: "4px",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05) translateY(-5px)",
                    boxShadow:
                      "0 10px 20px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                    zIndex: 1,
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "30%",
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                    zIndex: 1,
                  },
                  "&:hover::after": {
                    opacity: 1,
                  },
                }}
              >
                <img
                  src="/games/forza5.webp"
                  alt="Forza Horizon"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
              <ImpossibleButton />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
